"use client";

import React, { createContext, useState, useEffect, useContext } from "react";

type Theme = "light" | "dark";

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const [theme, setTheme] = useState<Theme>("dark");

  useEffect(() => {
    const stored = localStorage.getItem("theme") as Theme | null;
    if (stored) {
      setTheme(stored);
      document.documentElement.classList.toggle("dark", stored === "dark");
    } else {
      localStorage.setItem("theme", "dark");
    }
  }, []);

  useEffect(() => {
    // Ensure meta tags reflect theme
    const themeColorMeta = document.querySelector('meta[name="theme-color"]');
    const colorSchemeMeta = document.querySelector('meta[name="color-scheme"]');
  
    if (themeColorMeta) {
      themeColorMeta.setAttribute('content', theme === 'dark' ? '#000000' : '#ffffff');
    } else {
      const tag = document.createElement("meta");
      tag.name = "theme-color";
      tag.content = theme === 'dark' ? '#000000' : '#ffffff';
      document.head.appendChild(tag);
    }
  
    if (colorSchemeMeta) {
      colorSchemeMeta.setAttribute('content', theme);
    } else {
      const tag = document.createElement("meta");
      tag.name = "color-scheme";
      tag.content = theme;
      document.head.appendChild(tag);
    }
  
    document.documentElement.classList.toggle("dark", theme === "dark");
  }, [theme]);
  

    const toggleTheme = () => {
        const newTheme = theme === "dark" ? "light" : "dark";
        setTheme(newTheme);
        document.documentElement.classList.toggle("dark", newTheme === "dark");
        localStorage.setItem("theme", newTheme);
    };

return (
<ThemeContext.Provider value={{ theme, toggleTheme }}>
    {children}
</ThemeContext.Provider>
);
};

// Hook to use the context in child components
export const useTheme = () => {
    const ctx = useContext(ThemeContext);
    if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
    return ctx;
};
